import type { ReactNode } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import type { CommonProps } from '../../types';
import { CheckmarkSolidIcon, CloseSolidIcon } from '@beat-venues/ui/subset';

export interface FormMessageProps
    extends CommonProps,
        VariantProps<typeof FormMessageStyles> {
    children?: ReactNode;
}

export const FormMessageStyles = tv({
    slots: {
        root: 'rounded px-6 py-3 bg-gray-500 flex gap-4',
        icon: 'mt-1 shrink-0 w-4 text-center',
    },
    variants: {
        intent: {
            success: {
                root: 'text-success-500',
            },
            warning: {
                root: 'text-warning-500',
            },
            error: {
                root: 'text-danger-500',
            },
        },
    },
    defaultVariants: {
        intent: 'success',
    },
});

export const FormMessage = ({
    className,
    children,
    intent = 'success',
    testId = 'form-message',
    ...props
}: FormMessageProps): JSX.Element => {
    const { root, icon } = FormMessageStyles();

    const renderIcon = (intent: 'warning' | 'error' | 'success') => {
        switch (intent) {
            case 'warning':
                return (
                    <span
                        className={icon({ className: 'text-lg leading-none' })}
                        aria-hidden="true"
                    >
                        !
                    </span>
                );
            case 'error':
                return <CloseSolidIcon className={icon()} />;
            case 'success':
            default:
                return <CheckmarkSolidIcon className={icon()} />;
        }
    };
    return (
        <div
            className={root({ className, intent })}
            {...props}
            data-testid={testId}
        >
            {renderIcon(intent)}

            {children}
        </div>
    );
};
